// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import { fromUint8Array } from 'js-base64';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  ExpandMoreIcon,
} from '@simlin/diagram';

import { Project } from './Project';
import { User } from './User';
import { apiErrorMessage } from './api-error';
import { fetchTemplates, fetchTemplate, TemplateSummary } from './content-client';
import { projectFromJson } from '@simlin/core/datamodel';
import { Project as EngineProject } from '@simlin/engine';
import type { JsonProject } from '@simlin/engine';

import styles from './NewProject.module.css';
import typography from './typography.module.css';

interface NewProjectProps {
  user: User;
  onProjectCreated: (project: Project) => void;
}

interface NewProjectState {
  projectNameField: string;
  descriptionField: string;
  errorMsg?: string;
  expanded: boolean;
  projectPB?: Uint8Array;
  isPublic?: boolean;
  // D6 content library: the templates the user can start from, and the one
  // currently selected ('' = blank model).
  templates: TemplateSummary[];
  selectedTemplateSlug: string;
}

const readFile = (file: Blob): Promise<string> => {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onerror = (err) => {
      reader.abort();
      reject(new DOMException(`Problem parsing input file: ${err.type}`));
    };

    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsText(file);
  });
};

export class NewProject extends React.Component<NewProjectProps, NewProjectState> {
  state: NewProjectState;

  constructor(props: NewProjectProps) {
    super(props);
    this.state = {
      projectNameField: '',
      descriptionField: '',
      expanded: false,
      templates: [],
      selectedTemplateSlug: '',
    };
  }

  componentDidMount(): void {
    // Best-effort: the template picker is an optional enhancement, so a failed
    // fetch just leaves the picker hidden (templates stays empty).
    void fetchTemplates().then((templates) => {
      this.setState({ templates });
    });
    // C6.5: the Content Library "Use this template" link deep-links here as
    // /new?template=<slug>. Carrying the choice on the URL (rather than via
    // props) lets it survive the route remount between Home and NewProject.
    // Seed the project from it immediately, independent of the picker list.
    const slug = new URLSearchParams(window.location.search).get('template') ?? '';
    if (slug) {
      this.setState({ selectedTemplateSlug: slug });
      void this.loadTemplate(slug);
    }
  }

  // Seed the new project from a curated template by slug. Shared by the picker
  // dropdown and the /new?template=<slug> deep link.
  loadTemplate = async (slug: string): Promise<void> => {
    const detail = await fetchTemplate(slug);
    if (!detail) {
      this.setState({ errorMsg: 'Could not load the selected template.' });
      return;
    }
    await this.applyModelContents(detail.modelXmile, false);
  };

  handleProjectNameChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      projectNameField: event.target.value,
    });
  };

  handleDescriptionChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      descriptionField: event.target.value,
    });
  };

  setProjectName = async (): Promise<void> => {
    const bodyContents: {
      projectName: string;
      description: string;
      projectPB?: string;
      isPublic?: boolean;
    } = {
      projectName: this.state.projectNameField,
      description: this.state.descriptionField,
    };
    if (this.state.projectPB) {
      bodyContents.projectPB = fromUint8Array(this.state.projectPB);
    }
    if (this.state.isPublic) {
      bodyContents.isPublic = true;
    }
    const response = await fetch('/api/projects', {
      credentials: 'same-origin',
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyContents),
    });

    const status = response.status;
    if (!(status >= 200 && status < 400)) {
      const body = await response.json().catch(() => ({}));
      // A tier gate rejects a sub-Student save with { error: 'upgrade_required',
      // message } -- surface the human upgrade prompt, not the machine token (and
      // keep the username-collision hint as the fallback for other failures).
      const errorMsg = apiErrorMessage(body, `HTTP ${status}; maybe try a different username ¯\\_(ツ)_/¯`);
      this.setState({
        errorMsg,
      });
      return;
    }

    const project = (await response.json()) as Project;
    this.props.onProjectCreated(project);
  };

  handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleClose();
    }
  };

  handleClose = (): void => {
    if (this.state.projectNameField === '') {
      this.setState({
        errorMsg: 'Please give your project a non-empty name',
      });
    } else {
      setTimeout(this.setProjectName);
    }
  };

  handlePublicChecked = (checked: boolean): void => {
    this.setState({ isPublic: checked });
  };

  // Convert a model's source (XMILE, or Vensim `.mdl`) into the protobuf payload
  // sent on create, and stash it on state. Shared by the file-upload and
  // start-from-template paths.
  applyModelContents = async (contents: string, isVensim: boolean): Promise<void> => {
    try {
      const engineProject = isVensim ? await EngineProject.openVensim(contents) : await EngineProject.open(contents);

      const projectPB = await engineProject.serializeProtobuf();
      const json = JSON.parse(await engineProject.serializeJson()) as JsonProject;
      const activeProject = projectFromJson(json);
      const views = activeProject.models.get('main')?.views;
      if (!views || views.length === 0) {
        this.setState({
          errorMsg: `can't import model with no view at this time.`,
        });
        return;
      }

      this.setState({
        projectPB,
        errorMsg: undefined,
      });
    } catch (e) {
      this.setState({
        errorMsg: `${e}`,
      });
    }
  };

  uploadModel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target || !event.target.files || event.target.files.length <= 0) {
      console.log('expected non-empty list of files?');
      return;
    }
    const file = event.target.files[0];
    const contents = await readFile(file);
    // A file upload supersedes any template selection.
    this.setState({ selectedTemplateSlug: '' });
    await this.applyModelContents(contents, file.name.endsWith('.mdl'));
  };

  // D6 C6.4: start from a curated template. Load the selected template's model
  // so the new project is seeded with it; the blank option clears the seed.
  handleTemplateChange = async (event: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const slug = event.target.value;
    this.setState({ selectedTemplateSlug: slug });
    if (!slug) {
      this.setState({ projectPB: undefined, errorMsg: undefined });
      return;
    }
    await this.loadTemplate(slug);
  };

  render() {
    const warningText = this.state.errorMsg || '';
    return (
      <div className={styles.container}>
        <h2 className={typography.heading2}>Create a project</h2>
        <div className={styles.subtitle}>
          <p className={typography.subtitle1}>A project holds models and data, along with simulation results.</p>
        </div>
        <br />
        <TextField
          onChange={this.handleProjectNameChanged}
          autoFocus
          id="projectName"
          label="Project Name"
          type="text"
          error={this.state.errorMsg !== undefined}
          onKeyPress={this.handleKeyPress}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start">{this.props.user.id}/</InputAdornment>,
          }}
        />
        <br />
        <br />
        <TextField
          onChange={this.handleDescriptionChanged}
          id="description"
          label="Project Description"
          type="text"
          onKeyPress={this.handleKeyPress}
          fullWidth
        />

        <br />
        <br />

        {this.state.templates.length > 0 && (
          <div className={styles.templatePicker}>
            <label className={styles.templateLabel}>
              <span>Start from a template</span>
              <select
                className={styles.templateSelect}
                value={this.state.selectedTemplateSlug}
                onChange={this.handleTemplateChange}
              >
                <option value="">— blank model —</option>
                {this.state.templates.map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div>
              <span>Advanced</span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <form>
              <div className={styles.advancedGrid}>
                <div className={styles.gridCol8}>
                  <span>Use existing model</span>
                </div>
                <div className={styles.gridCol4}>
                  <Button variant="contained" className="NewProject-upload-button" color="secondary" component="label">
                    Select
                    <input
                      style={{ display: 'none' }}
                      accept=".stmx,.itmx,.xmile,.mdl"
                      id="xmile-model-file"
                      type="file"
                      onChange={this.uploadModel}
                    />
                  </Button>
                </div>
                <div className={styles.gridCol12}>
                  <FormControlLabel
                    control={<Checkbox checked={this.state.isPublic} onChange={this.handlePublicChecked} />}
                    label="Publicly accessible"
                  />
                </div>
              </div>
            </form>
          </AccordionDetails>
        </Accordion>

        <br />
        <br />
        <br />
        <p className={typography.subtitle2} style={{ whiteSpace: 'pre-wrap' }}>
          <b>{warningText || '\xa0'}</b>
        </p>
        <p className={typography.textRight}>
          <Button onClick={this.handleClose} color="primary">
            Create
          </Button>
        </p>
      </div>
    );
  }
}
